import RequestBuilder from './RequestBuilder';

const INVESTMENTS_BASE = 'investments';
const INVESTMENTS_CLIENTS = 'investments/clients';
const INVESTMENTS_FUNDS = 'investments/funds';
const INVESTMENTS_PRICES = 'investments/prices';
const INVESTMENTS_PENDING_CLIENT = 'investments/pendingchanges/client';
const INVESTMENTS_REFERENCES = 'investments/references';
const INVESTMENTS_TRANSACTIONS = 'investments/transactions';
const INVESTMENTS_CONFIGURATION = 'investments/configuration';

export default class AdviserHubInvestmentClient {
  static async getInvestmentUnitPrices() {
    return await RequestBuilder.makeGetRequest(`${INVESTMENTS_PRICES}/current`);
  }

  static async getPieGutSecurities(isActiveBoosterSecuritiesOnly?: boolean) {
    const url = `${INVESTMENTS_PRICES}/pie-gut-securities${
      isActiveBoosterSecuritiesOnly !== undefined
        ? `?isActiveBoosterSecurities=${isActiveBoosterSecuritiesOnly}`
        : ''
    }`;
    return await RequestBuilder.makeGetRequest(url);
  }

  static async getInvestmentClientDetails(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientdetails`
    );
  }

  static async getInvestmentClientContacts(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientcontacts`
    );
  }

  static async getInvestmentRegulars(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/regulars`
    );
  }

  static async getInvestmentTaxDetails(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientdetails/tax-details`
    );
  }

  static async getInvestmentBankAccounts(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientaccounts/bank-accounts`
    );
  }

  static async getInvestmentAccounts(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientaccounts/investment-accounts`
    );
  }

  static async getInvestmentAccountReturns(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientaccounts/returns`
    );
  }

  static async getInvestmentNatureAndPurpose(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientdetails/nature-and-purpose`
    );
  }

  static async getInvestmentClients(params: Record<string, any>) {
    const query = Object.entries(params)
      .map(([key, val]) => {
        if (key === 'sort') return `${key}=${val}`;
        return `${key}=${encodeURIComponent(val)}`;
      })
      .join('&');
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}?${query}`
    );
  }

  static async getInvestmentClientRegulars(params: Record<string, any>) {
    const query = Object.entries(params)
      .map(([key, val]) => {
        if (key === 'sort') return `${key}=${val}`;
        return `${key}=${encodeURIComponent(val)}`;
      })
      .join('&');
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_BASE}/regulars?${query}`
    );
  }

  static async getClientTypes() {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/client-types`
    );
  }

  static async getReportingCycles() {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_REFERENCES}/report-cycles`
    );
  }

  static async updateAccountDetail(clientId: string, params: any) {
    return await RequestBuilder.makePutRequest(
      `${INVESTMENTS_PENDING_CLIENT}/${clientId}/details`,
      params
    );
  }

  static async getBandRates(type: any) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_REFERENCES}/band-rates/${type}`
    );
  }

  static async updateTaxDetail(clientId: string, params: any) {
    return await RequestBuilder.makePutRequest(
      `${INVESTMENTS_PENDING_CLIENT}/${clientId}/tax-details`,
      params
    );
  }

  static async getBanks(countryCode: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_REFERENCES}/banks?countryCode=${countryCode}`
    );
  }

  static async updateBankDetails(clientId: string, params: any) {
    return await RequestBuilder.makePutRequest(
      `${INVESTMENTS_PENDING_CLIENT}/${clientId}/bank-account`,
      params
    );
  }

  static async getUsers(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientusers`
    );
  }

  static async addClientUser(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientusers`,
      request
    );
  }

  static async updateClientUser(clientId: string, params: any) {
    return await RequestBuilder.makePutRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientusers/${params.userId}`,
      params
    );
  }

  static async getTradeCutOff() {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CONFIGURATION}/trade-cutoff`
    );
  }

  static async getWithdrawalCutOff() {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CONFIGURATION}/withdrawal-cutoff`
    );
  }

  static async getClientsAvailableCash(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/available-cash`
    );
  }

  static async getClientsPieFunds(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/pie-funds`
    );
  }

  static async addClientContact(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_PENDING_CLIENT}/${clientId}/contact`,
      request
    );
  }

  static async getCountries() {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_REFERENCES}/countries`
    );
  }

  static async getInvestmentContactDetails(
    clientId: string,
    contactId: string
  ) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientcontacts/${contactId}/details`
    );
  }

  static async updateClientContact(clientId: string, params: any) {
    return await RequestBuilder.makePutRequest(
      `${INVESTMENTS_PENDING_CLIENT}/${clientId}/contact`,
      params
    );
  }

  static async getAddress(terms: string, count: number) {
    return await RequestBuilder.makeGetRequest(
      `/address/search?terms=${terms}&top=${count}`
    );
  }

  static async getRegularDetails(crcId: number) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_BASE}/regulars/${crcId}/details`
    );
  }

  static async validateIrdNumber(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/validate-ird-number`,
      request
    );
  }

  static async cashDeposit(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/cash-deposit`,
      request
    );
  }

  static async pieApplications(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/pie-applications`,
      request
    );
  }

  static async createInvestments(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/investments`,
      request
    );
  }

  static async createCashWithdrawal(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/cash-withdrawals`,
      request
    );
  }

  static async createPieRedemptions(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/pie-redemptions`,
      request
    );
  }

  static async createInvestmentSales(clientId: string, request: any) {
    return await RequestBuilder.makePostRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/sales`,
      request
    );
  }

  static async GetSecurityTransactions(
    cliendId: string,
    params: Record<string, any>
  ) {
    const query = Object.entries(params)
      .map(([key, val]) => {
        return `${key}=${encodeURIComponent(val)}`;
      })
      .join('&');
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_TRANSACTIONS}/${cliendId}/security?${query}`
    );
  }

  static async GetCashTransactions(
    cliendId: string,
    params: Record<string, any>
  ) {
    const query = Object.entries(params)
      .map(([key, val]) => {
        return `${key}=${encodeURIComponent(val)}`;
      })
      .join('&');
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_TRANSACTIONS}/${cliendId}/cash?${query}`
    );
  }

  static async getClientHoldings(clientId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_FUNDS}/${clientId}/holdings`);
  }

  static async getClientUser(clientId: string, userId: string) {
    return await RequestBuilder.makeGetRequest(
      `${INVESTMENTS_CLIENTS}/${clientId}/clientusers/${userId}/details`
    );
  }
}
